import { Link } from "react-router-dom";
import { useEffect, useRef, useCallback } from "react";
import {
  ArrowRight,
  Home,
  Zap,
  Shield,
  Heart,
  DollarSign,
  Users,
  ChevronDown,
  Leaf,
} from "lucide-react";

const HEADSHOT_URL =
  "https://customer-assets.emergentagent.com/job_144b4408-104d-4a3e-8d1d-a71d38c76165/artifacts/zozbmcst_Headshot%20for%20Front%20Page.jpg";

const HomePage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );
    observerRef.current = observer;

    const elements = document.querySelectorAll(".reveal");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only observer

  const policies = [
    {
      icon: Home,
      title: "Lower Housing & Rent",
      desc: "A real formula to cap excessive rents and make homeownership possible again.",
      path: "/policies/housing",
    },
    {
      icon: Zap,
      title: "Clean Energy Jobs",
      desc: "Make Nevada the solar capital of America. Lower bills. Thousands of new jobs.",
      path: "/policies/clean-energy",
    },
    {
      icon: Shield,
      title: "NDA Accountability",
      desc: "No more silence protecting misconduct. Full transparency for public officials.",
      path: "/policies/nda-reform",
    },
    {
      icon: DollarSign,
      title: "Pro-Worker Economy",
      desc: "Higher wages, fair scheduling, and protections that actually work — without hurting business.",
      path: "/policies/pro-worker",
    },
    {
      icon: Heart,
      title: "Healthcare Access",
      desc: "The eNVy plan: affordable healthcare built for Nevada, not insurance companies.",
      path: "/policies/healthcare",
    },
    {
      icon: Leaf,
      title: "Ending Food Deserts and Food Swamps",
      desc: "Fresh, affordable food in every Nevada neighborhood — not just the wealthy ones.",
      path: "/policies/food-deserts",
    },
  ];

  return (
    <div data-testid="homepage" className="bg-[#0B1A3B]">
      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16 md:pt-20">
        {/* Background layers */}
        <div className="absolute inset-0">
          {/* Base gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] via-[#0F2251] to-[#0B1A3B]" />
          {/* Radial glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[800px] bg-[#D4AF37]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#002B7F]/30 rounded-full blur-[100px]" />
          {/* Grid */}
          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage: `linear-gradient(rgba(212,175,55,1) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,1) 1px, transparent 1px)`,
              backgroundSize: "80px 80px",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 relative z-10 w-full">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-center">
            {/* Content — 3 cols */}
            <div className="lg:col-span-3 order-2 lg:order-1">
              {/* Primary badge */}
              <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white/[0.06] border border-white/10 backdrop-blur-md text-xs font-bold uppercase tracking-[0.15em] text-white/70 mb-8 rounded-full">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse" />
                Nevada Democratic Primary — June 9, 2026
              </div>

              <h1 className="text-[3.2rem] sm:text-[4.5rem] lg:text-[5.5rem] font-black leading-[0.9] tracking-tight text-white mb-6">
                Real Solutions.
                <br />
                <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                  Real Change.
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-white/50 max-w-lg leading-relaxed mb-4">
                I'm Cooper. A dad. A Nevadan for 35+ years. I'm running for
                Governor because working families deserve more than broken
                promises.
              </p>
              <p className="text-xl sm:text-2xl font-bold text-white mb-10">
                No empty promises. Just real solutions.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/policies/housing"
                  data-testid="hero-volunteer-btn"
                  className="group relative inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.35)]"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    See the Plan
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
                <Link
                  to="/spread-the-word"
                  data-testid="hero-learn-more-btn"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 hover:border-white/40 font-bold uppercase tracking-widest text-sm transition-all backdrop-blur-sm rounded-sm"
                >
                  Join the Movement
                </Link>
              </div>
            </div>

            {/* Image — 2 cols */}
            <div className="lg:col-span-2 order-1 lg:order-2 flex justify-center">
              <div className="relative">
                {/* Outer glow ring */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/20 via-transparent to-[#002B7F]/20 blur-2xl rounded-sm" />
                {/* Image container */}
                <div className="relative bg-gradient-to-br from-white/10 to-white/[0.02] p-1 backdrop-blur-sm border border-white/10">
                  <img
                    src={HEADSHOT_URL}
                    alt="James Cooper"
                    data-testid="hero-headshot"
                    className="w-full max-w-xs sm:max-w-sm object-cover"
                  />
                </div>
                {/* Corner accent */}
                <div className="absolute -bottom-2 -right-2 w-12 h-12 border-b-2 border-r-2 border-[#D4AF37]" />
                <div className="absolute -top-2 -left-2 w-12 h-12 border-t-2 border-l-2 border-[#D4AF37]" />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
          <ChevronDown className="w-5 h-5 text-white/20 animate-bounce" />
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] to-[#0F2251]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              What's broken
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
              Nevada Families Are Being Left Behind
            </h2>
          </div>

          <div className="reveal grid sm:grid-cols-3 gap-4">
            {[
              {
                stat: "40%",
                label: "of Nevadans are rent-burdened",
                color: "from-red-500/20 to-red-900/10",
              },
              {
                stat: "#50",
                label: "Nevada ranked last in healthcare access",
                color: "from-orange-500/20 to-orange-900/10",
              },
              {
                stat: "$0",
                label: "accountability for leaders hiding misconduct",
                color: "from-amber-500/20 to-amber-900/10",
              },
            ].map((item) => (
              <div
                key={item.stat}
                className={`relative p-6 bg-gradient-to-br ${item.color} border border-white/[0.06] backdrop-blur-sm`}
              >
                <p className="text-3xl sm:text-4xl font-black text-white mb-1">
                  {item.stat}
                </p>
                <p className="text-white/50 text-sm">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="reveal text-center mt-10">
            <p className="text-lg text-white/40 max-w-xl mx-auto">
              This isn't about left or right. It's about{" "}
              <span className="text-white font-bold">working families</span>{" "}
              who can't keep up — and a system that doesn't work for them.
            </p>
          </div>
        </div>
      </section>

      {/* ── THE SOLUTION ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        {/* Accent line */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              The plan
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
              15 Policies. Zero Fluff.
            </h2>
            <p className="text-white/40 text-lg max-w-xl mx-auto">
              Every policy is detailed, public, and built to actually work —
              not just sound good in a debate.
            </p>
          </div>

          <div className="reveal grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {policies.map((p) => {
              const Icon = p.icon;
              return (
                <Link
                  key={p.title}
                  to={p.path}
                  className="group relative p-6 bg-white/[0.03] border border-white/[0.06] hover:border-[#D4AF37]/40 hover:bg-white/[0.06] transition-all duration-300"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37] mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">
                    {p.title}
                  </h3>
                  <p className="text-white/40 text-sm leading-relaxed mb-4">
                    {p.desc}
                  </p>
                  <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Read more <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="reveal text-center mt-10">
            <Link
              to="/policies/more-coming-soon"
              className="inline-flex items-center gap-2 text-[#D4AF37] font-bold text-sm uppercase tracking-wider hover:text-[#F5D76E] transition-colors"
            >
              View all 15+ policies <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── WHY COOPER ── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F2251] to-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
                Not a politician
              </p>
              <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-6">
                Just a Dad Who's Had Enough
              </h2>
              <div className="space-y-4 text-white/50 leading-relaxed">
                <p>
                  I'm not a career politician. I'm a father. I've lived in
                  Nevada for over 35 years. I've watched rent double. I've
                  watched families leave. I've watched leaders make promises
                  and do nothing.
                </p>
                <p>
                  Every time I look at my son, I think about the Nevada he'll
                  grow up in. Will he be able to stay here? Will it be
                  affordable? Will the people in charge actually care?
                </p>
                <p className="text-white font-medium">
                  I decided to stop waiting for someone else to fix it.
                </p>
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-8 text-[#D4AF37] font-bold text-sm uppercase tracking-wider hover:text-[#F5D76E] transition-colors"
              >
                Read my full story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-px bg-gradient-to-br from-[#D4AF37]/20 to-transparent" />
                <div className="relative bg-white/[0.03] border border-white/10 p-1">
                  <img
                    src={HEADSHOT_URL}
                    alt="Cooper"
                    className="w-full max-w-sm object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VIDEO ── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0a0a0a]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Watch</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Meet Cooper</h2>
          </div>
          <div className="reveal max-w-4xl mx-auto">
            <div className="relative aspect-video bg-black border border-white/10 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/hNg2HNk6oaM?rel=0&modestbranding=1"
                title="Cooper for Nevada Campaign Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="campaign-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── MOMENTUM ── */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: "15+", label: "Detailed Solutions" },
              { value: "35", label: "Years in NV" },
              { value: "$0", label: "PAC Money" },
              { value: "1", label: "Mission: Nevada" },
            ].map((item) => (
              <div key={item.label}>
                <p className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
                  {item.value}
                </p>
                <p className="text-white/30 text-xs uppercase tracking-wider mt-1">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] to-[#0F2251]" />
        {/* Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.04] rounded-full blur-[100px]" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="reveal">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
              This Is Your Campaign
            </h2>
            <p className="text-white/40 text-lg max-w-xl mx-auto mb-10">
              I'm not asking for your money. I'm asking you to share this with
              one person who deserves to hear it.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/spread-the-word"
                data-testid="cta-volunteer-btn"
                className="group inline-flex items-center gap-2 px-10 py-5 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.35)]"
              >
                Share the Message
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/volunteer"
                data-testid="cta-contact-btn"
                className="inline-flex items-center gap-2 px-10 py-5 border border-white/20 text-white hover:bg-white/10 hover:border-white/40 font-bold uppercase tracking-widest text-sm transition-all backdrop-blur-sm"
              >
                Volunteer
              </Link>
            </div>

            <p className="mt-8 text-white/20 text-sm">
              Nevada Democratic Primary — June 9, 2026
            </p>
          </div>
        </div>
      </section>

      {/* CSS for reveal animations */}
      <style>{`
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .reveal.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
};

export default HomePage;
